/**
 * UI-owned discriminators for vulnerability resource views. Keeping these
 * values outside generated service clients lets migrated components remain
 * independent of the legacy ScanAnalysis package.
 */
export const LoginActivityType = {
  Success: 'success',
  Failed: 'failed',
  VmLogin: 'vm_login',
} as const;

export type LoginActivityType = typeof LoginActivityType[keyof typeof LoginActivityType];

export const ScanTypes = {
  Secrets: 'secrets',
} as const;

export type ScanTypes = typeof ScanTypes[keyof typeof ScanTypes];

export const SummaryType = {
  OperatingSystem: 'operating_system',
  Technology: 'technology',
  VulnerabilitySeverity: 'vulnerability_severity',
  CvssDistribution: 'cvss_distribution',
} as const;

export type SummaryType = typeof SummaryType[keyof typeof SummaryType];

export const VulnerabilitySeverity = {
  Critical: 'CRITICAL',
  High: 'HIGH',
  Medium: 'MEDIUM',
  Low: 'LOW',
  Unknown: 'UNKNOWN',
} as const;

export type VulnerabilitySeverity = typeof VulnerabilitySeverity[keyof typeof VulnerabilitySeverity];

export const VulnerabilityType = {
  Os: 'os',
  Application: 'application',
  Library: 'library',
} as const;

export type VulnerabilityType = typeof VulnerabilityType[keyof typeof VulnerabilityType];

export const FunnelStep = {
  VulnerablePackages: 'Vulnerable packages',
  InternetExposedPackages: 'Internet exposed packages',
  PackagesWithKnownExploits: 'Packages with known exploits',
  PatchablePackages: 'Patchable packages',
  HostsToPatch: 'Hosts to patch',
} as const;

export type FunnelStep = typeof FunnelStep[keyof typeof FunnelStep];

export const ApplicationEnum = {
  Memcached: 'Memcached',
  Redis: 'Redis',
  Nginx: 'Nginx',
  HaProxy: 'HAProxy',
  ApacheHttp: 'Apache HTTP',
  PostgreSql: 'PostgreSQL',
  MySql: 'MySQL',
  MariaDb: 'MariaDB',
  Kafka: 'Kafka',
  RabbitMq: 'RabbitMQ',
  MongoDb: 'MongoDB',
  Cassandra: 'Cassandra',
  CouchDb: 'CouchDB',
  Neo4j: 'Neo4j',
  GlooGateway: 'GlooGateway',
} as const;

export type ApplicationEnum = typeof ApplicationEnum[keyof typeof ApplicationEnum];

export const PatchRecommendationPackageSource = {
  Os: 'OS',
  Runtime: 'Runtime',
} as const;

export type PatchRecommendationPackageSource = typeof PatchRecommendationPackageSource[keyof typeof PatchRecommendationPackageSource];

export const LibraryType = {
  Nodejs: 'nodejs',
  Python: 'python',
  Java: 'java',
  Golang: 'golang',
  Ruby: 'ruby',
} as const;

export type LibraryType = typeof LibraryType[keyof typeof LibraryType];

export interface VulnerabilitySeverityCounts {
  critical?: number | null;
  high?: number | null;
  medium?: number | null;
  low?: number | null;
  unknown?: number | null;
}

export interface VulnerabilityPackageSummary {
  available_patches?: number | null;
  total?: number | null;
  vulnerability?: VulnerabilitySeverityCounts | null;
}

export interface VulnerabilityFinding {
  [key: string]: any;
  cve_id?: string | null;
  title?: string | null;
  description?: string | null;
  published_date?: string | null;
  last_modified_date?: string | null;
  cvss_v3?: Record<string, any> | null;
  cvss_v3_vector?: string | null;
  cvss_v3_score?: number | null;
  severity?: VulnerabilitySeverity | null;
  package_name?: string | null;
  installed_version?: string | null;
  fixed_in_version?: string | null;
  exploitable?: boolean | null;
}

export interface VulnerabilityContainerInfo {
  [key: string]: any;
  container_id?: string | null;
  image?: string | null;
  pod?: string | null;
  namespace?: string | null;
  instance_id?: string | null;
  account_id?: string;
  account_type?: string | null;
  region?: string;
  resource_uuid?: string | null;
  os_name?: string | null;
  os_version?: string | null;
}

/** Compatibility-shaped response types owned by the neutral vulnerability domain. */
export interface Vulnerability extends VulnerabilityFinding {
  application_name?: string | null;
  entities?: Record<string, any> | null;
}

export interface InstanceSummary {
  [key: string]: any;
}

export interface ContainerInfo extends VulnerabilityContainerInfo {
  [key: string]: any;
}

export interface ECRImageVisibility {
  [key: string]: any;
}

export interface ContainerImageMetadataRead {
  [key: string]: any;
}

export interface VulnerabilityPrioritization {
  [key: string]: any;
  host_types?: Array<{
    resource_type?: string | null;
    resource_count?: number | null;
  }>;
}

/** Dashboard response shapes shared by the legacy fallback and ScanVault adapter. */
export interface VulnerabilityDashboardTopItem {
  vulnerability_id?: string | null;
  cvss_score?: number | null;
  occurrences?: number | null;
  last_seen: string;
  patch_available?: boolean;
}

export interface VulnerabilityDashboardHighestItem
  extends VulnerabilityDashboardTopItem {
  vms_presence?: boolean;
  lambdas_presence?: boolean;
  containers_presence?: boolean;
  severity?: string | null;
}

export interface VulnerabilityDashboardLibraryItem {
  package_name?: string | null;
  occurrences?: number | null;
  patch_available?: boolean;
  patch_version?: string | null;
  severity?: string | null;
}

export interface VulnerabilityDashboardCount {
  vulnerability_count: Record<string, number>;
  current_count?: number;
  previous_count?: number;
  percentage_change?: number | string | null;
}

export interface VulnerabilityDashboardCriticalHigh {
  critical?: number;
  high?: number;
  new?: number;
  resolved?: number;
  change?: string | null;
}

export interface VulnerabilityDashboardBySources {
  items: Array<{
    vulnerability_count: Record<string, number>;
    agnostic_name: string;
  }>;
  clouds: string[];
}

export interface VulnerabilityBySeverityDashboard {
  critical: number;
  high: number;
  medium: number;
  low: number;
  unknown: number;
}

export interface VulnerablePackage {
  [key: string]: any;
  package_name?: string | null;
  installed_version?: string | null;
  package_category?: string | null;
  package_category_name?: string | null;
  vulnerability?: Record<string, any> | null;
  entities?: Record<string, any> | null;
  cve_ids?: string[] | null;
  patch_versions?: string[] | null;
  last_seen?: string | null;
}

export interface VulnerablePackagePage {
  items?: VulnerablePackage[] | null;
  total?: number;
  size?: number;
  page?: number;
}

export interface VulnerabilityCvePage {
  items: Record<string, any>[];
  total?: number;
  page?: number;
  size?: number;
}

export interface VulnerabilityPage {
  items?: Vulnerability[] | null;
  total?: number;
  page?: number;
  size?: number;
}

export interface VulnerabilityImageDashboard {
  items: ECRImageVisibility[];
  page: number;
  page_size: number;
  total: number;
}

export interface VulnerabilityPatchRecommendationResource {
  [key: string]: any;
  packages_to_patch?: VulnerablePackage[];
}
